import { Button, Table } from "reactstrap";
import React, { useState } from "react";
import {
  SESSION_STATUS,
  SESSION_UPLOAD_TOOLTIP_INCOMPLETE,
  SESSION_UPLOAD_TOOLTIP_PARTIAL,
  SESSION_UPLOAD_TOOLTIP_SUCCESS,
} from "../../constants";

import PropTypes from "prop-types";
import { CfSelect as Select } from "../../components/CfSelect";
import UploadSessionModal from "../../uploadsessions/uploadSessionModal";
import _ from "lodash";
import classnames from "classnames";
import { fixHeaders } from "../../components/dataTable";
import moment from "moment";
import { useFetch } from "../../hooks/useFetch";
import { useHistory } from "react-router-dom";

// Same options and same default as MissionsPanel: the two lists sit in the same
// tabs of the same page, they had no reason to sort in opposite directions.
const SORT_OPTIONS = [
  { value: "desc", label: "Newest first" },
  { value: "asc", label: "Oldest first" },
];

const IconSessionUpload = ({ status }) => (
  <i
    className={
      "fa-solid fa-lg fa-circle " +
      classnames({
        "text-success": [
          SESSION_STATUS.Completed,
          SESSION_STATUS.Synchronized,
        ].includes(status),
        "text-danger": [
          SESSION_STATUS.Initialized,
          SESSION_STATUS.InProgress,
        ].includes(status),
        "text-warning": [SESSION_STATUS.Partial].includes(status),
      })
    }
    data-tooltip-id="tooltip"
    data-tooltip-content={
      [SESSION_STATUS.Initialized, SESSION_STATUS.InProgress].includes(status)
        ? SESSION_UPLOAD_TOOLTIP_INCOMPLETE
        : [SESSION_STATUS.Partial].includes(status)
        ? SESSION_UPLOAD_TOOLTIP_PARTIAL
        : SESSION_UPLOAD_TOOLTIP_SUCCESS
    }
    data-tooltip-variant={
      [SESSION_STATUS.Initialized, SESSION_STATUS.InProgress].includes(status)
        ? "error"
        : [SESSION_STATUS.Partial].includes(status)
        ? "warning"
        : "success"
    }
    aria-hidden="true"
  />
);

IconSessionUpload.propTypes = { status: PropTypes.number };

// Upload sessions of a site (only when the campaign has an upload template).
export const UploadSessionsPanel = ({ site, contract }) => {
  const history = useHistory();
  const [sessions, { refetch }] = useFetch(
    `/api/client/sites/${site.id}/upload_sessions`
  );
  const [selected, setSelected] = useState(null);
  const [sort, setSort] = useState("desc");

  const visible = _.orderBy(
    sessions ?? [],
    ["acquisition_date"],
    [sort === "desc" ? "desc" : "asc"]
  );

  return (
    <div className="cf-section">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="cf-section__title mb-0">Upload sessions</h4>
        {/* "New upload session" now lives in the page header next to
            "View results": it is an action on the site, not on this list, and it
            was only reachable once you had opened this tab. */}
        {sessions?.length > 1 && (
          <Select
            className="cf-rs"
            classNamePrefix="cf-rs"
            isSearchable={false}
            options={SORT_OPTIONS}
            value={SORT_OPTIONS.find((option) => option.value === sort)}
            onChange={(option) => setSort(option.value)}
            aria-label="Sort upload sessions"
          />
        )}
      </div>

      {sessions === null ? (
        <div className="cf-placeholder">Loading sessions…</div>
      ) : sessions.length === 0 ? (
        <div className="cf-placeholder">No upload session exists.</div>
      ) : (
        <>
          {selected && (
            <UploadSessionModal
              us={selected}
              isOpen={true}
              closeModal={() => {
                setSelected(null);
                refetch();
              }}
              company={contract.company}
              withRedirect={true}
            />
          )}
          <Table className="cf-table" striped>
            {fixHeaders(
              " ",
              "Acquisition Date",
              "Created on",
              "Created by",
              " "
            )}
            <tbody>
              {visible.map((session) => (
                <tr
                  key={session.id}
                  onClick={() => setSelected(session)}
                  className="clickable-row"
                >
                  <td>
                    <IconSessionUpload status={session.status} />
                  </td>
                  <td>{session.acquisition_date}</td>
                  <td>
                    {moment(session.created_at).format("YYYY-MM-DD HH:mm:ss")}
                  </td>
                  <td>{session.user}</td>
                  <td>
                    {![
                      SESSION_STATUS.Completed,
                      SESSION_STATUS.Synchronized,
                    ].includes(session.status) && (
                      <Button
                        data-tooltip-id="tooltip"
                        data-tooltip-content="Edit"
                        className="p-0"
                        color="link"
                        onClick={(event) => {
                          event.stopPropagation();
                          history.push(`/upload/${session.id}`);
                        }}
                      >
                        <i className="fa-solid fa-lg fa-pencil" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </div>
  );
};

UploadSessionsPanel.propTypes = {
  site: PropTypes.object.isRequired,
  contract: PropTypes.object.isRequired,
};
